import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { adminApi } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

const IDLE_MS = 30 * 60 * 1000;

/**
 * 会话空闲 30 分钟自动登出（与设计文档 §1.1 对齐）
 */
export function useIdleLogout(enabled: boolean): void {
  const navigate = useNavigate();
  const clearSession = useAuthStore((s) => s.clearSession);

  const handleIdle = useCallback(async () => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      try {
        await adminApi.POST("/admin/v1/auth/logout");
      } catch {
        // 网络失败仍然清理前端态
      }
    }
    clearSession();
    navigate("/login", { replace: true, state: { reason: "idle" } });
  }, [clearSession, navigate]);

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setTimeout>;
    const bump = () => {
      clearTimeout(timer);
      timer = setTimeout(handleIdle, IDLE_MS);
    };

    bump();
    const events = [
      "keydown",
      "mousemove",
      "scroll",
      "touchstart",
      "click",
      "wheel",
    ] as const;
    events.forEach((e) =>
      window.addEventListener(e, bump, { passive: true })
    );
    const vis = () => {
      if (document.visibilityState === "visible") bump();
    };
    document.addEventListener("visibilitychange", vis);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      document.removeEventListener("visibilitychange", vis);
      clearTimeout(timer);
    };
  }, [enabled, handleIdle]);
}
